import PageHeading from '~/components/PageHeading';
import FeatureIntro from '~/components/FeatureIntro';
import React, { useState } from 'react';
import { useFlowQuery, useInvoicesQuery } from '~/lib/Queries';
import Loading from '~/components/Loading';
import Table from '~/components/Table';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import { formatDateShort } from '~/lib/DateTime';
import SectionPicker from '~/components/SectionPicker';
import FlowBuilder from '~/components/flows/FlowBuilder';
import { getBusinessId } from '~/lib/Api';
import useBusinessNavigate from '~/lib/useBusinessNavigate';
import Select from '~/components/Select';
import InvoiceStatusBadge from '~/components/invoices/InvoiceStatusBadge';
import PaintPaletteIcon from '~/components/icons/PaintPaletteIcon';
import InvoiceStylesPanel from '~/components/invoices/InvoiceStylesPanel';
import InvoiceSettings from '~/components/invoices/InvoiceSettings';
import SavedLineItemsPanel from '~/components/invoices/SavedLineItemsPanel';
import InvoiceRowMenu from '~/components/invoices/InvoiceRowMenu';

const InvoiceFlows = ({}) => {
    const flowableType = 'invoicebusiness';
    const flowableId = getBusinessId();
    const { data, isLoading } = useFlowQuery(flowableType, flowableId);
    const hasSteps = data?.steps?.length > 0;

    return (
        <div>
            {!isLoading && !hasSteps && (
                <div className="text-gray-500 max-w-lg">
                    Set up automations to send emails or SMS when invoices are sent, paid, or become overdue.
                    Click "Add Action" below to create your first automation.
                </div>
            )}
            <FlowBuilder
                flowableId={flowableId}
                flowableType={flowableType}
            />
        </div>
    );
}

const InvoicesList = ({ invoices, refetch }) => {
    const navigate = useBusinessNavigate();
    const [statusFilter, setStatusFilter] = useState('all');

    if (!invoices?.length) {
        return null;
    }

    const statusOptions = [
        { value: 'all', label: 'All Invoices' },
        { value: 'draft', label: 'Draft' },
        { value: 'sent', label: 'Sent' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
    ];

    const filteredInvoices = statusFilter === 'all'
        ? invoices
        : invoices.filter(invoice => invoice.status === statusFilter);

    return (
        <div className="max-w-2xl">
            <div className="mb-4 flex justify-end">
                <Select
                    options={statusOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    className="max-w-xs"
                />
            </div>

            <Table
                headers={[
                    'Number',
                    'Customer',
                    'Created',
                    'Due',
                    'Status',
                    'Amount',
                    '',
                ]}
                rowClassName="cursor-pointer"
                rows={filteredInvoices.map(invoice => [
                    invoice.invoice_number,
                    invoice.client?.name || invoice.client?.email,
                    formatDateShort(invoice.created_at),
                    invoice.due_at ? formatDateShort(invoice.due_at) : '',
                    <InvoiceStatusBadge status={invoice.status} />,
                    formatCurrency(invoice.total),
                    <InvoiceRowMenu invoice={invoice} refetch={refetch} />,
                ])}
                onClick={(row, index) => {
                    const invoice = filteredInvoices[index];
                    if (invoice) {
                        navigate(`/invoices/${invoice.id}`);
                    }
                }}
            />
        </div>
    );
};

const InvoicesIndexPage = ({}) => {
    const { data, isLoading, refetch } = useInvoicesQuery();
    const [section, setSection] = useState('list');
    const [isStylePanelOpen, setIsStylePanelOpen] = useState(false);
    const navigate = useBusinessNavigate();

    const invoices = data?.invoices || [];

    return (
        <div>
            <PageHeading
                heading="Invoices"
                rightContent={
                    section === 'list' && (
                        <div className="flex gap-2">
                            <button
                                className="btn-pink-pill uppercase font-semibold flex items-center"
                                type="button"
                                onClick={() => setIsStylePanelOpen(true)}
                            >
                                <PaintPaletteIcon className="w-5 h-5 mr-2" />
                                Styles
                            </button>
                            <button
                                type="button"
                                className="btn-pill"
                                onClick={() => navigate('/invoices/0')}
                            >
                                Create Invoice
                            </button>
                        </div>
                    )
                }
            />
            <FeatureIntro>
                Send professional invoices to your clients and track payments.
            </FeatureIntro>
            <SectionPicker
                options={[
                    { value: 'list', label: 'Invoices' },
                    { value: 'line-items', label: 'Line Items' },
                    { value: 'flows', label: 'Automations' },
                    { value: 'settings', label: 'Settings' },
                ]}
                value={section}
                onChange={setSection}
                className=""
            />

            {section === 'list' && (
                <>
                    {isLoading && <Loading />}
                    {!isLoading && !invoices?.length && (
                        <div className="panel px-6 py-4 text-gray-500 mt-6">
                            There are no invoices yet. Click "Create Invoice" to get started.
                        </div>
                    )}
                    {!isLoading && (
                        <InvoicesList
                            invoices={invoices}
                            refetch={refetch}
                        />
                    )}
                </>
            )}

            {section === 'line-items' && (
                <SavedLineItemsPanel />
            )}

            {section === 'flows' && (
                <InvoiceFlows />
            )}

            {section === 'settings' && (
                <InvoiceSettings />
            )}

            <InvoiceStylesPanel
                isOpen={isStylePanelOpen}
                onClose={() => setIsStylePanelOpen(false)}
            />
        </div>
    );
};

export default InvoicesIndexPage;

